import { action } from '@ember/object';
import { later } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type {
	ConnectWrapperObject,
	RequiredEnvs,
} from '@databox/ember-integrations/lib/components/Connect/Connect';
import type { ConnectEntryPointProps } from '@databox/ember-integrations/lib/components/Connect/ConnectEntryPoint';
import type { HostDataSourceType } from '@databox/ember-integrations/lib/types/host.types';

import type { Service } from 'webapp-types';
import { BillingFeature, SelectSourceOptionType } from 'webapp/enums/enums';
import { redirect } from 'webapp/utils/common';

import SourcePopupAfterConnectComponent from 'webapp/components/source/popup-after-connect';
import { Action as ForbiddenAction } from 'webapp/components/user/popup-forbidden-action';

import type SourceType from 'webapp/models-custom/source-type';

import { Defaults } from 'webapp/services/account';
import { Severity } from 'webapp/services/logger';

export interface ConnectSuccessMetadata {
	datasetFlowStarted?: boolean;
}

// the source the connect bundle reports on success, before it is pushed to the store
export type ConnectedSourceData = Parameters<
	NonNullable<ConnectEntryPointProps['onConnectSuccess']>
>[0];

export interface onConnectSourceSuccessResult {
	isSuccess: true;
	source: ConnectedSourceData;
	metadata?: ConnectSuccessMetadata;
}
export interface onConnectSourceErrorResult {
	isSuccess: false;
	error: string;
}
export type onConnectSourceResult = onConnectSourceSuccessResult | onConnectSourceErrorResult;

// called after a connect flow; source is set only on success
export type AfterConnectFn = (isSuccess: boolean, source?: ConnectedSourceData) => void;

export interface onAskTeammateToConnectResult {
	email: string;
	sourceType: SourceType;
}

export interface onFinishSourceSetupResult {
	isSuccess: boolean;
	error?: string;
}

export interface ConnectSourceOptions {
	origin?: string; // for tracking
	sourceId?: number | null; // reconnect / reauthorize / setup of an existing source
	reauthorize?: boolean;
	sourceSelected?: boolean;
	connectionId?: Parameters<ConnectWrapperObject['setupDataSource']>[2];
	connectSourceUsingConnectionDefaults?: boolean;
	isUseTemplate?: boolean;
	file?: File; // excel upload
	isSample?: boolean;
}

// The flows that continue an existing source.
type ExistingSourceConnectOptions = Pick<ConnectSourceOptions, 'origin'> & { sourceId: number };

export default class ConnectService extends EmberService {
	@service('logger') declare loggerService: Service.Logger;
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@tracked connectFunctions?: ConnectWrapperObject = undefined;
	@tracked afterConnectPopupDoneCallback?: (
		sourceData?: ConnectedSourceData,
		error?: string,
		metadata?: ConnectSuccessMetadata,
	) => void;

	get sourceTypes() {
		return this.mmSourceTypesService.sourceTypeModels;
	}

	get sqlConnectors(): string[] {
		return this.sourceTypes.filter((item) => item.isDb).map((item) => item.key);
	}

	get canCurrentUserConnect(): boolean {
		return (
			!this.accountService.onlyAdminsConnect ||
			!this.currentUserService.isLoggedIn || // conn flow
			this.currentUserService.isAdmin
		);
	}

	initialize() {
		// this are neccessary global variables for connect package
		const reactEnvs: RequiredEnvs = {
			APP_URL: this.accountService.getAccountAppUrl(),
			ACCOUNT_MANAGEMENT_APP_URL: this.accountService.getAccountAppUrl(),
			ACCOUNT_MANAGEMENT_API_URL: this.requestService.amaBackendUrl || '',
			ANALYTICS_APP_URL: window.location.origin,
			CONNECT_API_URL: this.requestService.connectApiUrl || '',
			CONNECT_PUSHER_CLUSTER: this.requestService.pusherCluster || '',
			CONNECT_PUSHER_KEY: this.requestService.pusherKey || '',
			DATASETS_API_URL: this.requestService.datasetsBackendUrl || '',
			UPVOTY_BASE_URL: '',
			UPVOTY_BOARD_HASH: '',
			ENVIRONMENT: window.env,
			DATASETS_PUSHER_KEY: this.requestService.datasetsPusherKey,
			DATASETS_PUSHER_CLUSTER: this.requestService.datasetsPusherCluster,
			DATASETS_SCHEMA_EXPLORER_URL: this.requestService.datasetsSchemaExplorerUrl,
			ANALYTICS_API_URL: this.requestService.apiUrl || '',
			PUSHER_KEY: this.requestService.pusherKey || '',
			PUSHER_CLUSTER: this.requestService.pusherCluster || '',
			PUSHER_ENV_ID: this.requestService.pusherEnvId ?? '',
			AI_BFF_URL: this.requestService.aiBffUrl ?? '',
			AUTH_API_URL: this.requestService.authApiUrl || '',
			AUTH_GOOGLE_CLIENT_ID: this.requestService.authGoogleClientId || '',
			HELP_URL: Defaults.HELP_URL,
			WEBSITE_URL: this.requestService.websiteUrl || '',
		};

		// ember-integrations reports to the webapp's DSN (the ephemeral's shared project), '' turns it off;
		// on production it's left out, so ember-integrations keeps its own Sentry project
		const sentryEnvs: { SENTRY_DSN?: string } =
			window.env === 'production' ? {} : { SENTRY_DSN: window.sentryConfig?.dsn ?? '' };

		// this is read in connect package
		window._env_ = { ...reactEnvs, ...sentryEnvs };
	}

	// entrypoint to connect flow.
	connectSource(
		sourceType: SourceType,
		options: ConnectSourceOptions = {},
	): Promise<onConnectSourceResult> {
		if (this.accountService.handleDemo()) {
			return Promise.reject();
		}

		if (
			sourceType.connectRedirectUrl &&
			!options.sourceId // skip for reconnect / reauthorize
		) {
			redirect(sourceType.connectRedirectUrl);
			return new Promise(() => {
				/* never resolve */
			});
		}

		const connectPromise = new Promise<{
			source: ConnectedSourceData;
			metadata?: ConnectSuccessMetadata;
		}>((resolve, reject) => {
			if (!this.canCurrentUserConnect) {
				this.openConnectDisabledPopup();
				reject('Only admins can connect');
				return;
			}

			if (
				sourceType.metricBuilderIntegration &&
				!this.billingService.canCreateCustomIntegration
			) {
				this.billingService.handleLimitations(
					BillingFeature.ADVANCED_DATA_SOURCES,
					'datasource_limitation',
					sourceType,
				);

				reject('API Metric Builder sources not allowed');
				return;
			}

			if (
				sourceType.isWorkflowIntegration &&
				!this.billingService.workflowIntegrationsEnabled
			) {
				this.billingService.handleLimitations(
					BillingFeature.WORKFLOW_INTEGRATIONS,
					'datasource_limitation',
					sourceType,
				);

				reject('Workflow sources not allowed');
				return;
			}

			if (
				sourceType.isSQLType &&
				!this.billingService.checkIfCan(BillingFeature.SQL_SOURCES)
			) {
				this.billingService.handleLimitations(
					BillingFeature.SQL_SOURCES,
					'datasource_limitation',
					sourceType,
				);

				reject('SQL Data sources not allowed');
				return;
			}

			if (sourceType.isPremium && !this.billingService.checkIfCan(BillingFeature.QB)) {
				this.billingService.handleLimitations(
					BillingFeature.QB,
					'datasource_limitation',
					sourceType,
				);

				reject('Premium Data sources not allowed');
				return;
			}

			if (
				sourceType.isSheets &&
				!this.billingService.checkIfCan(BillingFeature.SHEET_SOURCES)
			) {
				this.billingService.handleLimitations(
					BillingFeature.SHEET_SOURCES,
					'datasource_limitation',
					sourceType,
				);

				reject('Sheet sources not allowed');
				return;
			}

			if (sourceType.isToken && !this.billingService.dataPushEnabled) {
				this.billingService.handleLimitations(
					BillingFeature.DATA_PUSH,
					'datasource_limitation',
					sourceType,
				);

				reject('Custom Push Data sources not allowed');
				return;
			}

			if (
				!options.sourceId &&
				!this.billingService.checkIfCan(BillingFeature.SOURCES, true)
			) {
				reject('Too many Data sources');
				return;
			}

			this.afterConnectPopupDoneCallback = (source, error, metadata) => {
				if (source) {
					if (this.navigationService.isMainApp) {
						const isReconnect = !!options.sourceId;
						this.navigationService.appRoute.controller.send(
							'sourceConnected',
							{ ...source, key: sourceType.key },
							isReconnect,
						);
					}
					resolve({ source, metadata });
				} else {
					reject(error);
				}
			};

			if (options.file) {
				this.connectFunctions?.uploadExcelFile(sourceType, options.file, options.isSample);
			} else if (options?.sourceId) {
				if (options?.reauthorize) {
					this.connectFunctions?.reauthorizeSource(sourceType, options.sourceId);
				} else if (options?.sourceSelected && options.connectionId) {
					this.connectFunctions?.setupDataSource(
						sourceType,
						options.sourceId,
						options.connectionId,
					);
				} else {
					this.connectFunctions?.reconnectSource(sourceType, options.sourceId);
				}
			} else if (options?.connectSourceUsingConnectionDefaults) {
				if (sourceType.popup) {
					const { popupWidth, popupHeight } = sourceType;
					const windowOptions = `location=0, status=0, menubar=0, toolbar=0, width=${popupWidth}, height=${popupHeight}, scrollbars=yes`;
					const browserWindow = window.open('about:blank', '_blank', windowOptions);
					this.connectFunctions?.connectSourceUsingConnectionDefaults(
						sourceType,
						browserWindow ?? undefined,
					);
				} else {
					this.connectFunctions?.connectSourceUsingConnectionDefaults(sourceType);
				}
			} else {
				this.connectFunctions?.addNewConnection(sourceType, {
					isUseTemplate: options.isUseTemplate ?? false,
				});
			}
		});

		return new Promise((resolve) => {
			connectPromise
				.then(
					({ source, metadata }) => {
						resolve({ isSuccess: true, source, metadata });
					},
					(error: string) => {
						resolve({ isSuccess: false, error });
					},
				)
				.finally(() => {
					this.afterConnectPopupDoneCallback = undefined;
				});
		});
	}

	addNewDataset(type?: string | number, options?: { sourceId: number; dataSourceName: string }) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const dataSourceType = type && options ? this.getSourceType(type) : undefined;

		if (options && dataSourceType) {
			const existingSourceInfo = {
				sourceId: options.sourceId,
				name: options.dataSourceName,
				dataSourceType: dataSourceType,
			};

			this.connectFunctions?.addNewDataset(existingSourceInfo);
		} else {
			this.connectFunctions?.addNewDataset();
		}
	}

	editDatasetSetup(type: string | number, options: ExistingSourceConnectOptions) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const dataSourceType = this.getSourceType(type);

		if (!dataSourceType?.key) {
			this.loggerService.logError(
				'[ConnFatal] [SourceInfo] Source invalid',
				{
					type,
					origin: options.origin,
				},
				Severity.CRITICAL,
			);

			return;
		}

		this.connectFunctions?.editDatasetSetup(dataSourceType, options.sourceId);
	}

	connectSourceFromIdOrKey(
		type: string | number,
		options: ConnectSourceOptions,
	): Promise<onConnectSourceResult> {
		if (this.accountService.handleDemo()) {
			return Promise.resolve({ isSuccess: false, error: 'Action Not allowed' });
		}

		const dataSourceType = this.getSourceType(type);

		if (!dataSourceType?.key) {
			this.loggerService.logError(
				'[ConnFatal] [SourceInfo] Source invalid',
				{
					type,
					origin: options.origin,
				},
				Severity.CRITICAL,
			);

			return Promise.resolve({ isSuccess: false, error: 'No sourceType found' });
		}

		return this.connectSource(dataSourceType, options);
	}

	finishSourceSetup(
		type: string | number,
		options: ExistingSourceConnectOptions,
	): Promise<onFinishSourceSetupResult> {
		if (this.accountService.handleDemo()) {
			return Promise.resolve({ isSuccess: false, error: 'Action Not allowed' });
		}

		const dataSourceType = this.getSourceType(type);

		if (!dataSourceType?.key) {
			this.loggerService.logError(
				'[ConnFatal] [SourceInfo] Source invalid',
				{
					type,
					origin: options.origin,
				},
				Severity.CRITICAL,
			);

			return Promise.resolve({ isSuccess: false, error: 'No sourceType found' });
		}

		this.connectFunctions?.finishSourceSetup(dataSourceType, options.sourceId);

		return Promise.resolve({ isSuccess: true });
	}

	helpConnectSource(sourceType: HostDataSourceType): void {
		this.connectFunctions?.openAskTeammateToConnectDialog(sourceType);
	}

	getAfterConnectAction({ skipForDatasetDefaultSources = true } = {}) {
		// when source is connected, we want user to know how to create a board
		// source/popup-after-connect is a shortcut to wizard / sql article
		// to find out if this source qualifies for this popup, we need to load predefinedBlocks & publictemplates first
		const promises = [
			this.mmTemplatesService.requestBoardPublicTemplates(),
			this.mmMetricsService.requestMetrics(),
		];

		const afterConnectAction = (
			sourceType: SourceType,
			sourceId: number,
			metadata?: ConnectSuccessMetadata,
		) => {
			if (metadata?.datasetFlowStarted) {
				return;
			}

			// The connect package starts the dataset flow right after connecting SQL and sheet sources,
			// so neither the after-connect popup nor setupDataSource should run for them
			const isDatasetDefaultSource =
				(sourceType.isSqlConnector || sourceType.isSheets) &&
				sourceType.isDatasetSupported &&
				this.currentUserService.isOnboarded;

			if (skipForDatasetDefaultSources && isDatasetDefaultSource) {
				return;
			}

			void Promise.allSettled(promises).then(() => {
				const isSQL = this.sqlConnectors.includes(sourceType.key),
					isQbOnly = isSQL && sourceType.isQBSupported,
					sourceTypeId = sourceType.id!,
					metrics = this.mmMetricsService.getGenericMetricsForSourceTypeKey(
						sourceType.key,
					),
					publicTemplates = this.mmTemplatesService.boardPublicTemplateRecords.filter(
						(template) => {
							return template.sources.includes(sourceTypeId);
						},
					),
					isDatasetFlow = sourceType.isDatasetOnly,
					isAbstractSource =
						this.mmSourcesService.savedRecords.find((s) => s.id === String(sourceId))
							?.selectedSourceOption === SelectSourceOptionType.ABSTRACT;
				if (!isAbstractSource && (isQbOnly || metrics.length || publicTemplates.length)) {
					later(() => {
						const showPublicTemplates =
							publicTemplates.length > 0 && this.accountService.showPublicTemplates;

						const popup = this.popupService.createModal(
							SourcePopupAfterConnectComponent,
							{
								popupClass: 'popup-type-source-after-connect',
								allowStacked: true, // select dimension
							},
						);

						popup.open({
							sourceId,
							sourceType,
							isQbOnly,
							showPublicTemplates,
						});
					}, 1000);
				} else if (isDatasetFlow) {
					this.connectFunctions?.setupDataSource(sourceType, sourceId);
				}
			});
		};

		return afterConnectAction;
	}

	@action
	handleAfterInviteUserSuccessCallback() {
		this.infolayerService.ok(`Invitations are heading out to new team members...`);
	}

	openEmailVerificationInfoPopup() {
		this.currentUserService.openEmailVerificationInfoPopup(
			'ask your teammate to connect a data source',
		);
	}

	openConnectDisabledPopup() {
		this.currentUserService.openForbiddenActionPopup(ForbiddenAction.CONNECT);
	}

	private getSourceType(type: string | number): SourceType | undefined {
		let sourceType: SourceType | undefined;

		try {
			const givenType = typeof type === 'number' ? 'id' : 'key';
			sourceType = this.sourceTypes.find((item) => item[givenType] === type);
		} catch (_e) {
			// skip
		}

		return sourceType;
	}
}

declare module '@ember/service' {
	interface Registry {
		connect: ConnectService;
	}
}
